import type { StepId } from '../types/onboarding'

export type OnboardingPhase = {
  id: string
  label: string
  steps: StepId[]
}

/** Wizard steps before the progress bar begins (not shown on the stepper). */
export const PRE_PROGRESS_STEPS: StepId[] = ['welcome', 'checklist', 'important']

/** Phases shown on the progress bar (starts at General Questions). New Login Wizard is a separate step but not shown on the stepper. */
export const ALL_PROGRESS_BAR_PHASES: OnboardingPhase[] = [
  { id: 'general', label: 'General Questions', steps: ['generalQuestions'] },
  { id: 'attestation', label: 'Attestation', steps: ['attestation'] },
  { id: 'business-licenses', label: 'Business Licenses', steps: ['businessLicenses'] },
  { id: 'individual-licenses', label: 'Individual Licenses', steps: ['individualLicenses'] },
  { id: 'drlp', label: 'DRLP', steps: ['drlpAssignment'] },
  { id: 'logins', label: 'SL Logins', steps: ['logins'] },
  { id: 'contacts', label: 'Contacts', steps: ['contacts'] },
  { id: 'submit', label: 'Submit', steps: ['bor', 'otr', 'training', 'complete'] },
]

/** @deprecated Use `ALL_PROGRESS_BAR_PHASES`. */
export const PROGRESS_BAR_PHASES = ALL_PROGRESS_BAR_PHASES

export function isPreProgressStep(stepId: StepId): boolean {
  return PRE_PROGRESS_STEPS.includes(stepId)
}

export function showProgressBarForStep(stepId: StepId): boolean {
  return !isPreProgressStep(stepId)
}

export function phaseIndexForStep(stepId: StepId, phases: OnboardingPhase[]): number {
  if (isPreProgressStep(stepId)) return -1
  // Login follow-up steps are not on the stepper; treat them as SL Logins.
  const effectiveStepId =
    stepId === 'newLoginWizard' ||
    stepId === 'separateMiscAccountGuide' ||
    stepId === 'idahoAgreement' ||
    stepId === 'texasAdminAccountAccess' ||
    stepId === 'oregonNorthCarolinaUserAccountAccess' ||
    stepId === 'newYorkAdminAccountAccess' ||
    stepId === 'floridaGeorgiaMississippiUserAccountAccess' ||
    stepId === 'maineTaxPortalAccess' ||
    stepId === 'rhodeIslandTaxPortalAccess' ||
    stepId === 'vermontMyVaxAccess' ||
    stepId === 'newMexicoTapAccess'
      ? 'logins'
      : stepId
  const idx = phases.findIndex((p) => p.steps.includes(effectiveStepId))
  return idx === -1 ? 0 : idx
}

export function percentCompleteForStep(stepId: StepId, phases: OnboardingPhase[]): number {
  if (isPreProgressStep(stepId)) return 0
  const idx = phaseIndexForStep(stepId, phases)
  const max = phases.length - 1
  if (max <= 0) return 100
  return Math.round((idx / max) * 100)
}

/** First wizard step for a progress-bar phase (used when clicking a dot). */
export function stepIdForPhase(phase: OnboardingPhase): StepId {
  return phase.steps[0]
}
